<template>
  <div>
    <dt class="mb-2 font-semibold leading-none text-gray-900 dark:text-white">
      Stripe Customer
    </dt>
    <dd class="mb-4 font-light text-gray-500 sm:mb-5 dark:text-gray-400">
      <!-- Warning when no customer linked -->
      <div v-if="!searching && !customerFound" class="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p class="text-sm text-yellow-800 mb-3">
          No Stripe customer linked to this organization.
        </p>
        <button
          type="button"
          class="btn btn-primary"
          @click="searchCustomer">
          <i class="fas fa-search mr-2" />Search in Stripe by email
        </button>
      </div>

      <!-- Searching spinner -->
      <div v-if="searching" class="mt-4">
        <div class="flex items-center justify-center p-4">
          <i class="fas fa-spinner fa-spin text-blue-600 text-2xl" />
          <span class="ml-3 text-gray-600">Searching Stripe...</span>
        </div>
      </div>

      <!-- Customer found -->
      <div v-if="customerFound" class="mt-4">
        <div class="p-4 mb-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div class="flex items-center justify-between mb-3">
            <h4 class="font-semibold text-blue-900">
              Customer Found in Stripe
            </h4>
            <a
              :href="`https://dashboard.stripe.com/customers/${customer.id}`"
              target="_blank"
              class="btn btn-small btn-white">
              <i class="fas fa-external-link-alt mr-1" /> View in Stripe
            </a>
          </div>
          <dl class="text-sm mb-4">
            <dt class="font-medium text-blue-800">
              ID:
            </dt>
            <dd class="mb-2 text-blue-700 font-mono">
              {{ customer.id }}
            </dd>
            <dt class="font-medium text-blue-800">
              Email:
            </dt>
            <dd class="mb-2 text-blue-700">
              {{ customer.email || 'N/A' }}
            </dd>
            <dt class="font-medium text-blue-800">
              Name:
            </dt>
            <dd class="mb-2 text-blue-700">
              {{ customer.name || 'N/A' }}
            </dd>
            <dt class="font-medium text-blue-800">
              Created:
            </dt>
            <dd class="text-blue-700">
              {{ formatDate(customer.created) }}
            </dd>
          </dl>
          <button
            type="button"
            :disabled="linking"
            class="btn btn-secondary"
            @click="linkCustomer">
            <i :class="linking ? 'fas fa-spinner fa-spin mr-2' : 'fas fa-link mr-2'" />
            {{ linking ? 'Linking...' : 'Link Customer to Organization' }}
          </button>
        </div>
      </div>

      <!-- Not found message -->
      <div v-if="notFound" class="mt-4">
        <div class="p-4 mb-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p class="text-yellow-800">
            {{ errorMessage }}
          </p>
        </div>
        <button type="button" class="btn btn-link" @click="reset">
          Try again
        </button>
      </div>

      <!-- Error message -->
      <div v-if="error" class="mt-4">
        <div class="p-4 mb-4 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-red-800">
            {{ errorMessage }}
          </p>
        </div>
        <button type="button" class="btn btn-link" @click="reset">
          Try again
        </button>
      </div>
    </dd>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import axios from '@/init/axios'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  }
})

const searching = ref(false)
const customerFound = ref(false)
const notFound = ref(false)
const error = ref(false)
const linking = ref(false)
const customer = ref(null)
const errorMessage = ref('')

const searchCustomer = async() => {
  searching.value = true
  customerFound.value = false
  notFound.value = false
  error.value = false

  try {
    const { data } = await axios.post(`/ajax/organizations/${props.organizationId}/stripe_customer/search`)

    if (data.customer) {
      customer.value = data.customer
      customerFound.value = true
    } else {
      errorMessage.value = data.message || 'Customer not found in Stripe'
      notFound.value = true
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Search failed:', err)
    errorMessage.value = err.response?.data?.message || 'Failed to search Stripe customer'
    if (err.response?.status === 404) {
      notFound.value = true
    } else {
      error.value = true
    }
  } finally {
    searching.value = false
  }
}

const linkCustomer = async() => {
  linking.value = true

  try {
    await axios.post(`/ajax/organizations/${props.organizationId}/stripe_customer/link`, {
      customerId: customer.value.id
    })

    window.location.reload()
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Link failed:', err)
    errorMessage.value = err.response?.data?.error || 'Failed to link customer'
    error.value = true
    linking.value = false
  }
}

const reset = () => {
  customerFound.value = false
  notFound.value = false
  error.value = false
  customer.value = null
  errorMessage.value = ''
}

const formatDate = (timestamp) => new Date(timestamp * 1000).toLocaleDateString()
</script>
